import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from '@mui/material'
import moment from 'moment'
import * as React from 'react'
import type { CsvExportInterval } from '../../utils'

type Props = {
	open: boolean
	onClose: () => void
	onDownload: (
		startDate: string,
		endDate: string,
		fullColumns: boolean,
		exportInterval: CsvExportInterval,
		includeRaw: boolean,
	) => void
	showFullOption: boolean
	showIntervalOption: boolean
	showRawOption: boolean
}

const MAX_DAYS = 31

export default function DateRangeDownloadDialog({
	open,
	onClose,
	onDownload,
	showFullOption,
	showIntervalOption,
	showRawOption,
}: Props) {
	const today = moment().format('YYYY-MM-DD')
	const [startDate, setStartDate] = React.useState(today)
	const [endDate, setEndDate] = React.useState(today)
	const [fullColumns, setFullColumns] = React.useState(false)
	const [includeRaw, setIncludeRaw] = React.useState(false)
	const [exportInterval, setExportInterval] =
		React.useState<CsvExportInterval>('minute')

	const daysDiff = moment(endDate).diff(moment(startDate), 'days') + 1
	const isValid = startDate <= endDate && daysDiff <= MAX_DAYS && daysDiff > 0

	const handleDownload = () => {
		if (isValid) {
			// 生値の列は全項目 CSV にだけある (通常 CSV に土中温度の列が無い)
			onDownload(
				startDate,
				endDate,
				fullColumns,
				exportInterval,
				fullColumns && includeRaw,
			)
			onClose()
		}
	}

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogTitle>CSVダウンロード</DialogTitle>
			<DialogContent>
				<div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
					<TextField
						label="開始日"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
					<TextField
						label="終了日"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{ shrink: true }}
					/>
				</div>
				{isValid ? (
					<Typography variant="body2" style={{ marginTop: '8px' }}>
						{daysDiff}日分のデータをダウンロードします
					</Typography>
				) : (
					<Typography
						variant="body2"
						color="error"
						style={{ marginTop: '8px' }}
					>
						{daysDiff > MAX_DAYS
							? `最大${MAX_DAYS}日までです`
							: '開始日は終了日より前にしてください'}
					</Typography>
				)}
				{showFullOption && (
					<FormControlLabel
						control={
							<Checkbox
								checked={fullColumns}
								onChange={(e) => setFullColumns(e.target.checked)}
							/>
						}
						label="全項目"
						style={{ marginTop: '8px' }}
					/>
				)}
				{showRawOption && (
					<FormControlLabel
						control={
							<Checkbox
								checked={includeRaw}
								onChange={(e) => setIncludeRaw(e.target.checked)}
								disabled={!fullColumns}
							/>
						}
						label="生値"
						style={{ marginTop: '8px' }}
					/>
				)}
				{showIntervalOption && (
					<FormControl style={{ marginTop: '8px' }}>
						<FormLabel>出力間隔</FormLabel>
						<RadioGroup
							row
							value={exportInterval}
							onChange={(event) =>
								setExportInterval(event.target.value as CsvExportInterval)
							}
						>
							<FormControlLabel
								value="minute"
								control={<Radio />}
								label="通常"
							/>
							<FormControlLabel
								value="tenMinutes"
								control={<Radio />}
								label="10分平均"
							/>
							<FormControlLabel
								value="hour"
								control={<Radio />}
								label="1時間1件"
							/>
						</RadioGroup>
					</FormControl>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>キャンセル</Button>
				<Button
					onClick={handleDownload}
					variant="contained"
					color="primary"
					disabled={!isValid}
				>
					ダウンロード
				</Button>
			</DialogActions>
		</Dialog>
	)
}
